import React, { useState } from 'react';
import { Box, Plus, Edit2, Trash2, Save, X, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import useHiveTypes from '../hooks/useHiveTypes';
import { useFeatures } from '../context/FeatureContext';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FRAME_OPTIONS = ['Wooden', 'Plastic', '21st Century', 'BeeWise', 'Small Cell', 'Specialty', 'Flow Frames', 'Hoffman', 'Manley', 'SN4', 'SN5', 'DN4', 'DN5', 'Ideal'];
const FOUNDATION_OPTIONS = ['Wax', 'Plastic', 'Waxed Plastic', 'Specialised Frame', 'Strips', 'Foundationless', 'Small Cell Wax', 'Drone Cell', 'Thin Surplus'];
const BOX_OPTIONS = ['Deep', 'Medium/Ideal', 'Shallow', '8-Frame', '10-Frame', 'Full Depth', 'WSP', 'Half Depth', 'Flow Super', 'National Deep', 'National Shallow', 'Commercial', 'Nucleus (NUC)', 'Top Bar Body'];
const FEATURE_OPTIONS = ['Queen Excluder', 'Screened Bottom Board', 'Solid Bottom Board', 'Inner Cover', 'Telescoping Cover', 'Migratory Cover', 'Entrance Reducer', 'Mouse Guard', 'Ventilation Board', 'Feeder (Top)', 'Feeder (Frame)', 'Observation Window', 'Flow Key Access', 'Slatted Rack'];

const ToggleGroup = ({ label, options, selected, onChange }) => (
  <div className="space-y-2">
    <label className="text-sm font-medium text-stone-700">{label}</label>
    <div className="flex flex-wrap gap-1.5">
      {options.map(opt => {
        const isSelected = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => {
              onChange(isSelected ? selected.filter(s => s !== opt) : [...selected, opt]);
            }}
            className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${
              isSelected
                ? 'bg-amber-600 text-white border-amber-600'
                : 'bg-white text-stone-600 border-stone-300 hover:border-amber-300 hover:bg-amber-50'
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  </div>
);

const ManageHiveTypes = () => {
  const { user } = useAuth();
  const { adminTypes, userTypes, refresh } = useHiveTypes();
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '',
    frame_types: [], foundation_types: [], box_types: [], features: []
  });

  const plan = user?.subscription_plan || 'free';
  const isAdmin = user?.role === 'admin';
  const { freePlatform } = useFeatures();
  const canCreate = isAdmin || freePlatform || plan !== 'free';

  const resetForm = () => {
    setForm({ name: '', description: '', frame_types: [], foundation_types: [], box_types: [], features: [] });
    setEditingId(null);
    setShowForm(false);
  };

  const startEdit = (type) => {
    setForm({
      name: type.name || '',
      description: type.description || '',
      frame_types: type.frame_types || [],
      foundation_types: type.foundation_types || [],
      box_types: type.box_types || [],
      features: type.features || []
    });
    setEditingId(type.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      if (editingId) {
        await axios.put(`${API}/config/user-hive-types/${editingId}`, form, { headers });
        toast.success(`"${form.name}" updated`);
      } else {
        await axios.post(`${API}/config/user-hive-types`, form, { headers });
        toast.success(`"${form.name}" created`);
      }
      resetForm();
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? Hives using this type will keep their data.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/config/user-hive-types/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`"${name}" deleted`);
      refresh();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-4" data-testid="manage-hive-types">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
            <Box className="h-6 w-6 text-amber-600" />
            My Hive Types
          </h2>
          <p className="text-sm text-stone-500 mt-0.5">Create custom hive configurations for your operation</p>
        </div>
        {canCreate && !showForm && (
          <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-amber-600 hover:bg-amber-700" data-testid="add-hive-type-btn">
            <Plus className="h-4 w-4 mr-1" /> New Type
          </Button>
        )}
      </div>

      {/* Upsell for free plan */}
      {!canCreate && (
        <Card className="p-4 bg-amber-50 border-amber-200" data-testid="hive-types-upsell">
          <div className="flex items-start gap-3">
            <Lock className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-amber-800">Custom Hive Types — Hobby Plan & Above</p>
              <p className="text-sm text-amber-700 mt-1">
                Create hive configurations tailored to your equipment. Define frame types, foundations, box sizes, and features unique to your operation.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2 border-amber-400 text-amber-700 hover:bg-amber-100"
                onClick={() => window.location.href = '/?view=subscription'}
              >
                View Plans
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Create / Edit Form */}
      {showForm && (
        <Card className="p-4 border-amber-300 bg-amber-50/50" data-testid="hive-type-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-stone-800">{editingId ? 'Edit Hive Type' : 'New Custom Hive Type'}</h3>
            <button onClick={resetForm} className="text-stone-400 hover:text-stone-600"><X className="h-5 w-5" /></button>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-stone-700">Name *</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Modified Commercial"
                  className="mt-1"
                  data-testid="hive-type-name-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Description</label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Optional description"
                  className="mt-1"
                />
              </div>
            </div>

            <ToggleGroup label="Frame Types" options={FRAME_OPTIONS} selected={form.frame_types} onChange={(v) => setForm({ ...form, frame_types: v })} />
            <ToggleGroup label="Foundation Types" options={FOUNDATION_OPTIONS} selected={form.foundation_types} onChange={(v) => setForm({ ...form, foundation_types: v })} />
            <ToggleGroup label="Box / Super Types" options={BOX_OPTIONS} selected={form.box_types} onChange={(v) => setForm({ ...form, box_types: v })} />
            <ToggleGroup label="Hive Features & Components" options={FEATURE_OPTIONS} selected={form.features} onChange={(v) => setForm({ ...form, features: v })} />

            <div className="flex gap-2 pt-2">
              <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="save-hive-type-btn">
                <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </Button>
              <Button variant="outline" onClick={resetForm}>Cancel</Button>
            </div>
          </div>
        </Card>
      )}

      {/* Admin Default Types (read-only) */}
      {adminTypes.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-stone-500 uppercase tracking-wide mb-2">Default Types (Admin)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {adminTypes.map(type => (
              <Card key={type.id} className="p-3 bg-white border-stone-200">
                <div className="font-semibold text-stone-700 text-sm">{type.name}</div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {(type.frame_types || []).slice(0, 3).map(f => (
                    <span key={f} className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{f}</span>
                  ))}
                  {(type.foundation_types || []).slice(0, 2).map(f => (
                    <span key={f} className="text-[10px] bg-green-50 text-green-600 px-1.5 py-0.5 rounded">{f}</span>
                  ))}
                  {((type.frame_types?.length || 0) + (type.foundation_types?.length || 0)) === 0 && (
                    <span className="text-[10px] text-stone-400">No config</span>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* User Custom Types */}
      {userTypes.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-stone-500 uppercase tracking-wide mb-2">My Custom Types</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {userTypes.map(type => (
              <Card key={type.id} className="p-3 bg-amber-50/50 border-amber-200">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-stone-800 text-sm">{type.name}</div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => startEdit(type)} className="p-1 text-stone-400 hover:text-amber-600" data-testid={`edit-type-${type.id}`}>
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => handleDelete(type.id, type.name)} className="p-1 text-stone-400 hover:text-red-600" data-testid={`delete-type-${type.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                {type.description && <div className="text-xs text-stone-500 mt-0.5">{type.description}</div>}
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {(type.frame_types || []).map(f => (
                    <span key={f} className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{f}</span>
                  ))}
                  {(type.foundation_types || []).map(f => (
                    <span key={f} className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded">{f}</span>
                  ))}
                  {(type.box_types || []).map(f => (
                    <span key={f} className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">{f}</span>
                  ))}
                  {(type.features || []).map(f => (
                    <span key={f} className="text-[10px] bg-stone-100 text-stone-600 px-1.5 py-0.5 rounded">{f}</span>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {canCreate && userTypes.length === 0 && !showForm && (
        <Card className="text-center p-6 border-dashed border-stone-300">
          <Box className="h-10 w-10 text-stone-300 mx-auto mb-2" />
          <p className="text-stone-500 text-sm">No custom hive types yet. Click "New Type" to create one.</p>
        </Card>
      )}
    </div>
  );
};

export default ManageHiveTypes;
